import { useContext, useEffect } from 'react';
import { fireImpression } from '@vp/ab-impression';
import { OptimizelyExperimentsContext } from '../components/contexts/OptimizelyExperimentsContext';

const OPTIMIZELY_KEY = process.env.OPTIMIZELY_SDK_KEY;

async function fireOptimizelyImpression(experimentKey, variationKey) {
  if (!experimentKey || !variationKey) {
    console.warn('no experiment provided');
    return;
  }

  await fireImpression(experimentKey, variationKey, OPTIMIZELY_KEY);
}

/**
 * Hook that returns whether the given experimentKey and variationKey should be used to change an experience
 * (i.e. the experimentKey and variationKey combo exists in the activeOptimizelyExperiments).
 * Also manages firing impressions for "live" optimizely experiments.
 *
 * @param {*} experimentKey
 * @param {*} variationKey
 * @param {*} experimentWillBeUsed optional, whether or not the experiment will be used. Intended to be used by experiments that require a precondition to be met to change an experience.
 */
export default function useExperiment(
  experimentKey,
  variationKey,
  experimentWillBeUsed = true
) {
  const { activeOptimizelyExperiments } = useContext(
    OptimizelyExperimentsContext
  );

  const foundExperiment = activeOptimizelyExperiments.find(
    experiment => experiment.experimentKey === experimentKey
  );
  const experimentIsValid = !!(
    foundExperiment?.experimentKey && foundExperiment?.variationKey
  );

  useEffect(() => {
    if (experimentIsValid) {
      if (foundExperiment.shouldFireImpression && experimentWillBeUsed) {
        fireOptimizelyImpression(experimentKey, foundExperiment.variationKey);
      }
    }
  }, [experimentIsValid, experimentKey, foundExperiment, experimentWillBeUsed]);

  if (!experimentKey || !variationKey) {
    console.error('Illegal Arguments passed to useExperiment.');
    return false;
  }

  if (!activeOptimizelyExperiments) {
    console.error(
      `Unable to determine if experiment: ${experimentKey} exists due to empty active experiments.`
    );
    return false;
  }

  if (!foundExperiment) {
    console.error(
      `Experiment: ${experimentKey} not found in active experiments.`
    );
    return false;
  }

  if (!experimentWillBeUsed) {
    // precondition for experiment to be used is not met
    return false;
  }

  if (foundExperiment.variationKey !== variationKey) {
    // passed in variation is not selected
    return false;
  }

  return true;
}
